import { Fragment, ReactNode, useCallback, useMemo } from 'react';

import { ChangedNodes, TreeNode } from './types';
import { traverseTreeChildren } from './helpers/traverseTreeChildren';
import { useMappedCheckboxState } from './helpers/useMappedCheckboxState';
import { useExpandedState } from './helpers/useExpandedState';
import { getParentCheckboxState } from './helpers/getParentCheckboxState';
import { setParentNode } from './helpers/setParentNode';
import { updateTreeNodeChildrenSelectedState } from './helpers/updateTreeNodeChildrenSelectedState';

/**
 * Пропсы для рендера дерева чекбоксов
 */
export interface RenderTreeProps {
    /** Текущий дерева */
    item: TreeNode;
    /** Функция для изменения состояния чекбокса */
    setChecked: (value: boolean) => void;
    /** Состояние задизейбленности чекбокса */
    isDisabled?: boolean;
    /** Состояние чекбокса */
    isChecked: boolean;
    /** Функция для изменения состояния раскрытия аккордеона */
    setExpanded: (expanded: boolean) => void;
    /** Состояние раскрытия аккордеона */
    isExpanded: boolean;
    /** Уровень вложенности */
    level: number;
}

/**
 * Обновляет состояние узла и его потомков
 */
function updateNodeAndChildren({
    itemId,
    value,
    nodesMap,
    initialCheckboxValue,
    prevSelectedState,
}: {
    itemId: string;
    value: boolean;
    nodesMap: Map<string, TreeNode>;
    initialCheckboxValue: boolean;
    prevSelectedState: Map<string, boolean>;
}) {
    const modifiedNew = new Map(prevSelectedState);
    const item = nodesMap.get(itemId);

    if (!item) {
        throw new Error(`Node with id ${itemId} not found`);
    }

    const parentState = getParentCheckboxState({
        item,
        checkboxMap: modifiedNew,
        initialCheckboxValue,
    });

    if (parentState !== value) {
        modifiedNew.set(item.id, value);
    } else {
        // Если у родителя то же состояние чекбокса, то удаляем изменение. Оно унаследуется от родителя
        modifiedNew.delete(item.id);
    }

    updateTreeNodeChildrenSelectedState({
        item,
        newState: value,
        prevSelectedState,
        selectedState: modifiedNew,
    });

    return modifiedNew;
}

/**
 * Функция, управляющая состоянием дерева чекбоксов.
 * Чекбоксы в этом дереве не зависят от дочерних чекбоксов, то есть если выбрать все чекбоксы внутри родителя,
 * то родитель не поменяет своего значения. (так работает дерево регионов)
 * @param nodes узлы дерева
 * @param initialCheckboxValue значение чекбоксов по умолчанию (все выключены или все включены)
 * @param changedNodes состояние чекбоксов (не всех в дереве, только измененных)
 * @param setChangedNodes функция для обработки изменения состояния чекбоксов
 */
export function useCheckboxTreeState({
    nodes,
    initialCheckboxValue = false,
    setChangedNodes,
    changedNodes,
    renderCollapsed,
    forcedExpandedIds,
}: {
    nodes: TreeNode[];
    initialCheckboxValue?: boolean;
    changedNodes: ChangedNodes;
    setChangedNodes: (update: (oldNodes: ChangedNodes) => ChangedNodes) => void;
    renderCollapsed?: boolean;
    forcedExpandedIds?: ReadonlySet<string>;
}) {
    const nodesWithParents = useMemo(() => nodes.map(item => setParentNode(item)), [nodes]);
    const nodesMap = useMemo(() => {
        const result = new Map<string, TreeNode>();
        traverseTreeChildren(nodesWithParents, node => result.set(node.id, node));

        return result;
    }, [nodesWithParents]);

    const expandedNodesIds = useMemo(
        () =>
            Array.from(nodesMap.values())
                /* Фильтруем узлы, которые не могут быть раскрыты/свернуты,
                 * нужна явная проверка на false тк isExpandable является опциональным
                 **/
                .filter(node => node.isExpandable === false)
                .map(node => node.id),
        [nodesMap],
    );

    // Состояние раскрытия аккордеонов узлов
    const { getNodeIsExpanded, setExpanded, collapseAll } = useExpandedState(expandedNodesIds, forcedExpandedIds);

    const setNodeValue = useCallback(
        (itemId: string, value: boolean) => {
            setChangedNodes(modifiedOld => {
                const prevSelectedState = new Map(modifiedOld);
                const modifiedNew = updateNodeAndChildren({
                    itemId,
                    value,
                    nodesMap,
                    initialCheckboxValue,
                    prevSelectedState,
                });
                return [...modifiedNew.entries()];
            });
        },
        [initialCheckboxValue, nodesMap, setChangedNodes],
    );

    const setNodesValues = useCallback(
        (values: Array<[string, boolean]>) => {
            setChangedNodes(modifiedOld => {
                const prevSelectedState = new Map(modifiedOld);
                const modifiedNew = new Map(prevSelectedState);

                values.forEach(([itemId, value]) => {
                    const updated = updateNodeAndChildren({
                        itemId,
                        value,
                        nodesMap,
                        initialCheckboxValue,
                        prevSelectedState: modifiedNew,
                    });
                    updated.forEach((value, key) => modifiedNew.set(key, value));
                    [...modifiedNew.keys()].forEach(key => {
                        if (!updated.has(key)) {
                            modifiedNew.delete(key);
                        }
                    });
                });

                return [...modifiedNew.entries()];
            });
        },
        [initialCheckboxValue, nodesMap, setChangedNodes],
    );

    const changedNodesMap = useMappedCheckboxState(changedNodes, nodesWithParents, initialCheckboxValue);

    /**
     * Функция отрисовки дерева.
     * Мемоизация в этом месте работать не будет!
     * Функция рендера нужна для 2-х целей
     * 1. Отрисовка дерева с правильным уровнем (инкрементация каждый раз при погружении)
     * 2. Получение состояния элемента относительно родителя (об этом ниже)
     *
     * Если родитель был ☑ и ребенок не был изменен, то он тоже будет ☑.
     * Это позволяет хранить только измененные узлы, не дублируя дерево целиком, так как дерево может быть огромным
     * и его придется полностью копировать каждый раз при изменении состояния.
     */
    function renderChildren({
        children,
        defaultValue,
        level,
        renderer,
    }: {
        children: TreeNode[];
        defaultValue: boolean;
        level: number;
        renderer: (props: RenderTreeProps) => React.ReactNode;
    }): ReactNode {
        if (children.length === 0) {
            return null;
        }

        const renderedChildren = children
            .map(item => {
                const renderedItem = renderer({
                    item,
                    isDisabled: item.isDisabled,
                    setChecked: setNodeValue.bind(null, item.id),
                    // Если этот чекбокс меняли, то берем противоположное значение от родительского
                    isChecked: changedNodesMap.get(item) ?? defaultValue,
                    isExpanded: getNodeIsExpanded(item.id),
                    setExpanded: setExpanded.bind(null, item.id),
                    level,
                });

                const renderedChildren =
                    (renderCollapsed || getNodeIsExpanded(item.id)) &&
                    item.children &&
                    Boolean(item.children?.length) &&
                    renderChildren({
                        children: item.children ?? [],
                        // В зависимости от родителя устанавливаем дефолт
                        defaultValue: changedNodesMap.get(item) ?? defaultValue,
                        // Дети должны быть на уровень ниже
                        level: level + 1,
                        renderer: renderer,
                    });

                return renderedItem || renderedChildren ? (
                    <Fragment key={item.id}>
                        {renderedItem}
                        {renderedChildren}
                    </Fragment>
                ) : null;
            })
            .filter(Boolean);

        if (renderedChildren.length === 0) {
            return null;
        }

        return renderedChildren;
    }

    return {
        /** Мемоизированная функция установки состояния чекбокса */
        setNodeValue,
        /** Мемоизированная функция установки состояния множества чекбоксов */
        setNodesValues,
        /** Мемоизированная функция установки состояния аккордеона  */
        setExpanded,
        /** Мемоизированная функция сворачивания всех аккордеонов  */
        collapseAll,
        /** Состояние измененных узлов  */
        changedNodes: changedNodesMap,
        /** Функция для получения состояния раскрытия аккордеона для узла  */
        getNodeIsExpanded,

        /**
         * Функция отрисовки дерева.
         * Сама контролирует уровень вложенности и отрисовку дочерних компонентов
         * @param renderer функция отрисовки компонента дерева
         * @param initialLevel начальный уровень отступа
         */
        renderTree(renderer: (props: RenderTreeProps) => ReactNode, initialLevel: number = 1) {
            return renderChildren({
                children: nodesWithParents,
                defaultValue: initialCheckboxValue,
                level: initialLevel,
                renderer: renderer,
            });
        },
    };
}
